import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex, type FlexProps } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo } from "react";

import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  getOrderDetailsFromFulfillmentGroups,
  getOrderDetailsInfoFromRedoOrder,
} from "@redotech/redo-merchant-app-common/order-details-info";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Order } from "@redotech/redo-order-core-common/order";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PreloadedOrderInfo,
  useOrderNavigation,
} from "../fulfillment-groups/order-navigation-context";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

interface NavigationGroup {
  id: string;
  orderId: string;
  isRedoOrder: boolean;
}

const WINDOW_OFFSETS = [-2, -1, 0, 1, 2];

export const OrderNavigation = memo(function OrderNavigation({
  currentFulfillmentGroupId,
  pb = "xl",
}: {
  currentFulfillmentGroupId: string;
  pb?: FlexProps["pb"];
}) {
  const {
    fulfillmentGroupInfo,
    orderPagePrefetchedInfo,
    resetOrderPagePrefetchedInfo,
  } = useOrderNavigation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const merchantClient = useRequiredContext(RedoMerchantRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const currIndex = useMemo(() => {
    return fulfillmentGroupInfo.findIndex(
      (group) => group.id === currentFulfillmentGroupId,
    );
  }, [fulfillmentGroupInfo, currentFulfillmentGroupId]);

  const totalCount = useMemo(() => {
    return fulfillmentGroupInfo.length;
  }, [fulfillmentGroupInfo]);

  const getOrderDetailsFromRedoOrder = async (orderId: string | undefined) => {
    if (!orderId) {
      return undefined;
    }
    const { order } = await merchantClient.getOrder({
      orderId,
      teamId: team._id.toString(),
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return getOrderDetailsInfoFromRedoOrder(order as unknown as Order);
  };

  const getOrderDetailsFromNonRedoOrder = async (
    externalOrderId: string | undefined,
  ) => {
    if (!externalOrderId) {
      return undefined;
    }
    const { fulfillmentGroups } = await rpcClient.getFulfillmentGroupsByOrderId(
      { externalOrderId },
    );
    const result = getOrderDetailsFromFulfillmentGroups(
      fulfillmentGroups,
      externalOrderId,
    );
    return result;
  };

  useLoad(async () => {
    if (currIndex < 0 || currIndex >= fulfillmentGroupInfo.length) {
      return;
    }

    const windowGroups = WINDOW_OFFSETS.map(
      (offset) => fulfillmentGroupInfo[currIndex + offset],
    ).filter((group): group is NavigationGroup => !!group);

    const loaded = await Promise.all(
      windowGroups.map(async (group) => {
        const params = group.isRedoOrder
          ? { orderId: group.orderId }
          : { orderExternalId: group.orderId };
        const getOrderDetails = group.isRedoOrder
          ? getOrderDetailsFromRedoOrder
          : getOrderDetailsFromNonRedoOrder;

        const [orderDetailsInfo, fulfillmentGroupData] =
          await Promise.allSettled([
            getOrderDetails(group.orderId),
            rpcClient.getFulfillmentGroupsForOrder(params),
          ]);

        if (
          orderDetailsInfo.status === "fulfilled" &&
          fulfillmentGroupData.status === "fulfilled" &&
          orderDetailsInfo.value
        ) {
          return {
            orderId: group.orderId,
            info: {
              orderInfo: orderDetailsInfo.value,
              fulfillmentGroupInfo:
                fulfillmentGroupData.value.fulfillmentGroups,
            },
          };
        }
        return undefined;
      }),
    );

    const prefetchedInfo: Record<string, PreloadedOrderInfo> = {};
    for (const entry of loaded) {
      if (entry) {
        prefetchedInfo[entry.orderId] = entry.info;
      }
    }
    resetOrderPagePrefetchedInfo(prefetchedInfo);

    return true;
  }, [currIndex, fulfillmentGroupInfo, resetOrderPagePrefetchedInfo]);

  const navigate = useNavigate();
  // OMS-3209: preserve the source fulfillment view across prev/next so the
  // breadcrumb keeps pointing back to wherever the user opened the order
  // from. The param isn't needed for navigation itself — it only feeds the
  // detail page's breadcrumb logic — but losing it on every arrow click
  // would silently re-break the fix.
  const [searchParams] = useSearchParams();
  const sourceViewParam = (() => {
    const sv = searchParams.get("sourceView");
    return sv ? `&sourceView=${encodeURIComponent(sv)}` : "";
  })();

  const encodeOrderIdForNavigation = (
    orderId: string,
    isRedoOrder: boolean,
  ): string => {
    return isRedoOrder ? orderId : btoa(orderId);
  };

  const getDestinationTab = (order: NavigationGroup): string => {
    const destinationFulfillmentGroups =
      orderPagePrefetchedInfo[order.orderId]?.fulfillmentGroupInfo;
    return (
      destinationFulfillmentGroups?.find((group) => group._id === order.id)
        ?._id ??
      destinationFulfillmentGroups?.[0]?._id ??
      order.id
    );
  };

  const prev = useHandler(() => {
    if (currIndex > 0) {
      const prevOrder = fulfillmentGroupInfo[currIndex - 1];
      if (!prevOrder) {
        return;
      }
      const outboundOrderTab = getDestinationTab(prevOrder);
      const encodedOrderId = encodeOrderIdForNavigation(
        prevOrder.orderId,
        prevOrder.isRedoOrder,
      );
      navigate(
        `../${encodedOrderId}?outboundOrderTab=${outboundOrderTab}&isRedoOrder=${prevOrder.isRedoOrder}${sourceViewParam}`,
      );
    }
  });

  const next = useHandler(() => {
    if (currIndex < totalCount - 1) {
      const nextOrder = fulfillmentGroupInfo[currIndex + 1];
      if (!nextOrder) {
        return;
      }
      const outboundOrderTab = getDestinationTab(nextOrder);
      const encodedOrderId = encodeOrderIdForNavigation(
        nextOrder.orderId,
        nextOrder.isRedoOrder,
      );
      navigate(
        `../${encodedOrderId}?outboundOrderTab=${outboundOrderTab}&isRedoOrder=${nextOrder.isRedoOrder}${sourceViewParam}`,
      );
    }
  });

  const handleKeyDown = useHandler((event: KeyboardEvent) => {
    if (
      document.activeElement &&
      (document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA" ||
        document.activeElement.getAttribute("contenteditable") === "true")
    ) {
      return;
    }

    switch (event.key) {
      case "ArrowUp":
      case "ArrowLeft":
        if (currIndex > 0) {
          event.preventDefault();
          prev();
        }
        break;
      case "ArrowDown":
      case "ArrowRight":
        if (currIndex < totalCount - 1) {
          event.preventDefault();
          next();
        }
        break;
    }
  });

  useEffect(() => {
    addEventListener("keydown", handleKeyDown);

    return () => {
      removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  if (fulfillmentGroupInfo.length === 0) {
    return null;
  }

  return (
    <Flex align="center" dir="row" gap="xl" pb={pb}>
      <Flex align="center">
        <Flex dir="row" gap="sm">
          <Text fontSize="xs">{currIndex === -1 ? "-" : currIndex + 1}</Text>
          <Text fontSize="xs" textColor="placeholder">
            / {totalCount}
          </Text>
        </Flex>
        <RedoButton
          aria-label="Previous"
          disabled={currIndex <= 0}
          hierarchy="secondary"
          IconLeading={ChevronUpIcon}
          onClick={prev}
          size="sm"
        />
        <RedoButton
          aria-label="Next"
          disabled={currIndex === totalCount - 1}
          hierarchy="secondary"
          IconLeading={ChevronDownIcon}
          onClick={next}
          size="sm"
        />
      </Flex>
    </Flex>
  );
});
